import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Jeep Wrangler (YJ/TJ classic, ~1995) — a short, upright, boxy 4x4 at true
// scale (1 unit = 1 metre): length ~3.88 (the tailgate spare adds a little),
// width 1.68 (about 1.9 over the chunky tyres), height 1.77 (tall), wheelbase
// 2.37, on chunky 0.38-radius off-road tyres. Nose is +Z. Signature cues: the
// ICONIC SEVEN-SLOT grille (seven dark vertical bars in the flat body-colour
// front panel) flanked by round headlamps; a flat, near-upright fold-down
// windscreen with exposed hinges; a flat hood with two front hood catches; a
// removable BLACK hard top over the cabin (the contrasting Wrangler top); flat
// black plastic fender flares; round side indicators; a flat black steel front
// bumper with round fog lamps; big chunky off-road tyres on five-spoke alloys;
// and a full-size spare wheel on the swing-away tailgate. Painted bright Jeep
// red with a black top, black flares and black bumpers.

const PAINT = "#c0392b"; // iconic bright Jeep red — the "paint" slot default
const TOP = "#1b1c1e"; // removable BLACK hard top (unslotted contrasting top)
const TRIM = "#17181b"; // gloss-black trim (pillars, hinges, latches, brackets)
const FLARE = "#1e1f21"; // matte-black flat plastic fender flares
const CLAD = "#26282c"; // matte plastic cladding (sills, aprons)
const BUMPER = "#2c2f31"; // black steel bumpers
const WELL = "#141518"; // wheel wells
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SILVER = "#adb3ba"; // skid plate
const RIMDARK = "#202225"; // alloy rim dish
const SPOKE = "#c4c9cf"; // bright five-spoke alloy
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp / fog-lamp glass
const REFLECT = "#8fa5b2"; // headlamp reflector
const SLOT = "#1c1d1f"; // the dark seven-slot grille bars
const AMBER = "#e8a13a";
const RED = "#a91527";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.38; // chunky off-road tyre
const WHEEL_X = 0.78; // half track (tyre outer faces proud of the flares)
const AXLE_F = 1.18;
const AXLE_R = -1.19; // wheelbase 2.37
const BODY_HW = 0.78; // slab body half width (1.56 over the panels)
const NOSE = 1.92; // flat front panel face (grille)
const TAIL = -1.92; // rear body face (tailgate; spare stands proud of it)
const BELT = 1.06; // beltline / top of the body tub / door line
const ROOF_Y0 = 1.48; // bottom of the black hard top
const ROOF_Y1 = 1.71; // top of the black hard top (spec-tall)

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One chunky off-road wheel on a five-spoke alloy: wide tyre, brake disc,
 *  recessed dark rim dish, five bright spokes slightly proud of the tyre face,
 *  chrome centre cap. Every part shares `name` so the `drive` track spins them
 *  as one node. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.32, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.18, 0.18, 0.03, 16, BRAKE, [x - side * 0.08, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.24, 0.24, 0.09, 18, RIMDARK, [x + side * 0.085, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.06, 0.42, 0.1, SPOKE, [x + side * 0.135, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.07, 0.07, 0.03, 12, CHROME, [x + side * 0.15, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well (a dark disc proud of the body side) plus the
 *  Wrangler's flat black plastic fender flare — a slim horizontal cladding box
 *  over the wheel with two short vertical end caps. The flare is flat and low,
 *  standing just proud of the slab side (never a tall monster-truck tent). */
function arch(side: 1 | -1, z: number): Part[] {
  const fx = side * (BODY_HW + 0.02);
  return [
    cyl(0.48, 0.48, 0.04, 18, WELL, [side * 0.86, WHEEL_R, z], [0, 0, Math.PI / 2]),
    box(0.11, 0.18, 1.12, FLARE, [fx, 0.82, z]), // flat flare top, over the wheel
    box(0.11, 0.46, 0.14, FLARE, [fx, 0.6, z + 0.49]), // front flare wall
    box(0.11, 0.46, 0.14, FLARE, [fx, 0.6, z - 0.49]), // rear flare wall
  ];
}

// The six hazard indicators: round front turn signals on the grille panel,
// round side markers on the front fenders, and amber rear indicators. Parts and
// blink tracks are generated from one table so the pivots (each part's own
// centre) can never drift from the parts.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; kind: "front" | "side" | "rear" }> = [
  { name: "indicator-fl", pos: [-0.56, 0.7, 1.95], kind: "front" },
  { name: "indicator-fr", pos: [0.56, 0.7, 1.95], kind: "front" },
  { name: "indicator-ml", pos: [-0.8, 1.02, 1.5], kind: "side" },
  { name: "indicator-mr", pos: [0.8, 1.02, 1.5], kind: "side" },
  { name: "indicator-rl", pos: [-0.62, 0.62, -1.94], kind: "rear" },
  { name: "indicator-rr", pos: [0.62, 0.62, -1.94], kind: "rear" },
];

function indicatorPart(ind: (typeof INDICATORS)[number]): Part {
  switch (ind.kind) {
    case "front":
      return cyl(0.055, 0.055, 0.04, 14, AMBER, ind.pos, [Math.PI / 2, 0, 0], { name: ind.name });
    case "side":
      return cyl(0.035, 0.035, 0.04, 12, AMBER, ind.pos, [0, 0, Math.PI / 2], { name: ind.name });
    case "rear":
      return box(0.18, 0.1, 0.05, AMBER, ind.pos, undefined, { name: ind.name });
  }
}

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.055, 0.055, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.4, 0.14, 3.2, TRIM, [0, 0.4, 0])); // underbody / chassis pan (high clearance)
  P.push(box(1.44, 0.14, 0.3, CLAD, [0, 0.4, 1.9])); // apron under the nose, back to the front wells
  P.push(box(1.44, 0.14, 0.3, CLAD, [0, 0.4, -1.9])); // apron under the tail, back to the rear wells

  // -- Body masses (paint): one boxy "bathtub", a flat hood, flat fender tops --
  P.push(paint(2 * BODY_HW, BELT - 0.42, NOSE - TAIL, [0, (0.42 + BELT) / 2, 0])); // main tub, sill to beltline
  P.push(paint(1.0, 0.06, 1.13, [0, 1.09, 1.285])); // flat hood, proud of the tub top, front edge behind the grille
  for (const s of S) P.push(paint(0.28, 0.06, 1.13, [s * 0.62, 1.09, 1.285])); // flat fender tops flanking the hood
  for (const s of S) P.push(box(0.06, 0.05, 0.05, TRIM, [s * 0.3, 1.1, 1.83])); // two hood catches at the front

  // -- Greenhouse: flat windscreen + BLACK hard top ---------------------------
  P.push(box(1.35, 0.04, 0.1, TRIM, [0, 1.08, 0.76])); // cowl at the windscreen base
  P.push(box(1.42, 0.48, 0.045, SCREEN, [0, 1.3, 0.7], [-0.1, 0, 0])); // flat, near-upright windscreen
  for (const s of S) P.push(paint(0.1, 0.5, 0.09, [s * 0.68, 1.3, 0.7], [-0.1, 0, 0])); // A-pillars (body-colour frame)
  for (const s of S) P.push(box(0.1, 0.06, 0.06, TRIM, [s * 0.55, 1.05, 0.79])); // windscreen fold hinges
  for (const s of S) P.push(box(0.34, 0.014, 0.03, TRIM, [s * 0.3, 1.1, 0.72], [-0.1, 0, 0])); // wipers

  // Black hard top: a flat box over the cabin, front edge over the windscreen
  // header, rear edge capping the tailgate; full body width so it reads as one
  // sealed slab (no glass may poke past it in front/back views).
  const topCz = (0.7 + TAIL) / 2;
  const topD = 0.7 - TAIL;
  P.push(box(2 * BODY_HW, ROOF_Y1 - ROOF_Y0, topD, TOP, [0, (ROOF_Y0 + ROOF_Y1) / 2, topCz]));
  for (const s of S) P.push(box(0.03, 0.04, topD - 0.1, TOP, [s * 0.785, ROOF_Y0 + 0.03, topCz])); // roof drip rails (within roof width)
  P.push(box(0.3, 0.03, 0.02, RED, [0, ROOF_Y0 - 0.03, TAIL - 0.03])); // high brake light on the tailgate top
  // Solid black rear-quarter sides of the hard top (beltline to roof, behind
  // the doors), with a small fixed quarter window let into each.
  for (const s of S) {
    P.push(box(0.03, ROOF_Y0 - BELT, 1.0, TOP, [s * 0.782, (BELT + ROOF_Y0) / 2, -1.05])); // rear quarter panel
    P.push(box(0.02, 0.3, 0.6, GLASS, [s * 0.79, 1.3, -1.02])); // fixed quarter window
  }
  // Door glass band + pillars on each slab side (recessed inboard of the body).
  for (const s of S) {
    P.push(box(0.02, ROOF_Y0 - BELT - 0.04, 0.96, GLASS, [s * 0.77, (BELT + ROOF_Y0) / 2, 0.0])); // door glass
    P.push(box(0.04, ROOF_Y0 - BELT, 0.06, TOP, [s * 0.783, (BELT + ROOF_Y0) / 2, -0.52])); // B-pillar (hard-top black)
    P.push(box(0.02, 0.025, 2.0, CHROME, [s * 0.785, BELT - 0.02, -0.4])); // belt-line strip
  }

  // -- Front fascia: seven-slot grille, round headlamps, steel bumper ---------
  // THE Jeep cue: seven dark vertical bars in the flat body-colour front panel,
  // clearly countable, flanked by round headlamps.
  for (let i = -3; i <= 3; i++) {
    P.push(box(0.055, 0.28, 0.03, SLOT, [i * 0.1, 0.91, NOSE + 0.02])); // seven grille slots
  }
  for (const s of S) {
    P.push(cyl(0.15, 0.15, 0.03, 16, CHROME, [s * 0.56, 0.92, NOSE + 0.01], [Math.PI / 2, 0, 0])); // round lamp bezel
    P.push(cyl(0.125, 0.125, 0.05, 16, LAMP, [s * 0.56, 0.92, NOSE + 0.03], [Math.PI / 2, 0, 0])); // round headlamp
    P.push(cyl(0.05, 0.05, 0.02, 12, REFLECT, [s * 0.56, 0.92, NOSE + 0.06], [Math.PI / 2, 0, 0])); // reflector
  }
  P.push(box(1.5, 0.14, 0.16, BUMPER, [0, 0.5, NOSE + 0.04])); // flat black steel front bumper, narrower than the track
  for (const s of S) P.push(cyl(0.06, 0.06, 0.04, 14, LAMP, [s * 0.45, 0.5, NOSE + 0.13], [Math.PI / 2, 0, 0])); // round fog lamps
  P.push(box(0.7, 0.08, 0.06, SILVER, [0, 0.4, NOSE + 0.14])); // skid plate under the bumper
  P.push(box(0.34, 0.1, 0.02, PLATE, [0, 0.64, NOSE + 0.025])); // front number plate (below the grille)

  // -- Rear fascia: body-colour tailgate, swing-away spare, square lamps ------
  P.push(paint(1.5, 0.46, 0.04, [0, 1.29, TAIL - 0.02])); // upper tailgate panel (beltline to hard top)
  P.push(box(1.0, 0.2, 0.03, GLASS, [0, 1.4, TAIL - 0.04])); // rear window in the tailgate, proud of it
  // Full-size spare bolted to the swing-away tailgate (faces rearward, no spin).
  P.push(box(0.32, 0.34, 0.1, TRIM, [0.05, 0.98, TAIL - 0.05])); // spare carrier bracket
  P.push(cyl(WHEEL_R, WHEEL_R, 0.28, 18, TIRE, [0.05, 0.98, TAIL - 0.11], [Math.PI / 2, 0, 0])); // spare tyre
  P.push(cyl(0.24, 0.24, 0.08, 18, RIMDARK, [0.05, 0.98, TAIL - 0.2], [Math.PI / 2, 0, 0])); // spare rim dish
  for (let k = 0; k < 5; k++) {
    const a = (k * 2 * Math.PI) / 5;
    P.push(box(0.05, 0.32, 0.08, SPOKE, [0.05, 0.98, TAIL - 0.22], [a, 0, 0])); // spare spokes
  }
  P.push(cyl(0.07, 0.07, 0.03, 12, CHROME, [0.05, 0.98, TAIL - 0.24], [Math.PI / 2, 0, 0])); // spare centre cap
  for (const s of S) P.push(box(0.18, 0.28, 0.06, RED, [s * 0.62, 0.8, TAIL - 0.02])); // square red tail lamps
  P.push(box(1.5, 0.14, 0.16, BUMPER, [0, 0.5, TAIL - 0.04])); // flat black steel rear bumper
  P.push(box(0.34, 0.1, 0.02, PLATE, [0.42, 0.72, TAIL - 0.03])); // rear number plate beside the spare

  // -- Side furniture: sills, handles, exposed hinges, mirrors ----------------
  for (const s of S) {
    P.push(box(0.05, 0.1, 1.5, CLAD, [s * 0.79, 0.5, 0])); // sill / rocker strip, well to well
    P.push(box(0.03, 0.03, 0.14, CHROME, [s * 0.79, 0.85, 0.05])); // door handle
    P.push(box(0.04, 0.1, 0.05, TRIM, [s * 0.785, 0.95, 0.5])); // exposed door hinge (upper)
    P.push(box(0.04, 0.1, 0.05, TRIM, [s * 0.785, 0.6, 0.5])); // exposed door hinge (lower)
    P.push(box(0.09, 0.03, 0.04, TRIM, [s * 0.8, 1.15, 0.6])); // mirror arm
    P.push(paint(0.08, 0.12, 0.1, [s * 0.87, 1.14, 0.57])); // mirror head
    P.push(box(0.06, 0.1, 0.012, CHROME, [s * 0.87, 1.14, 0.51])); // mirror glass
  }

  for (const ind of INDICATORS) P.push(indicatorPart(ind));

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const jeepWrangler = defineModel({
  id: "jeep-wrangler",
  name: "Jeep Wrangler",
  batchId: "cars",
  tags: ["car", "jeep", "wrangler", "suv", "offroad", "classic"],
  summary: "A classic Jeep Wrangler (YJ/TJ) at true scale, in bright Jeep red with a black hard top.",
  description:
    "A classic Jeep Wrangler (YJ/TJ, ~1995) at real 1 unit = 1 metre scale " +
    "(about 3.88 long, 1.68 wide, 1.77 tall, 2.37 wheelbase, chunky " +
    "0.38-radius off-road tyres). All the Wrangler cues: the iconic SEVEN-SLOT " +
    "grille (seven dark vertical bars in the flat body-colour front panel) " +
    "flanked by round headlamps; a flat, near-upright fold-down windscreen with " +
    "exposed hinges; a flat hood with two front catches; a removable BLACK hard " +
    "top over the cabin; flat black plastic fender flares; round side " +
    "indicators; a flat black steel front bumper with round fog lamps; chunky " +
    "off-road tyres on five-spoke alloys; and a full-size spare on the " +
    "swing-away tailgate with square tail lamps. Body panels carry the 'paint' " +
    "recolour slot (bright Jeep red); `drive` spins the four road wheels (the " +
    "spare stays still) and `hazards` blinks the round front lamps, fender " +
    "side markers and rear ambers.",
  author: "Fable 5",
  footprint: 2.1,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
